<script>
  import { onMount, onDestroy } from 'svelte';
  import NavBarReusable from './NavbarReusable.svelte';
  import { generateTeacherPerformanceMonths } from './teacherPerformanceMonths.js';

  // State for submission and data
  let loading = true;
  let submitError = null;
  let retentionData = null;
  let commentsData = null;
  let completionData = null;
  let surveyData = null;
  let paymentData = null;
  let bonusData = null;
  let teacherInfo = null;
  let isUserAdmin = false;
  let mounted = true;
  let showAllPaymentClasses = false;

  // State for month selection
  let selectedMonth = '';
  let availableMonths = [];

  // Check if user is admin
  async function checkIfUserIsAdmin() {
    const token = localStorage.getItem('jwtToken');
    if (!token) {
      isUserAdmin = false;
      return;
    }

    // Create a timeout promise for admin check
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Admin check timeout')), 10000); // 10 second timeout
    });

    try {
      const responsePromise = fetch('/studentplayground/api/check_admin_status/', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const response = await Promise.race([responsePromise, timeoutPromise]);

      if (response.ok) {
        const data = await response.json();
        isUserAdmin = data.is_admin || false;
      } else {
        console.warn('Admin status check failed:', response.status);
        isUserAdmin = false;
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      isUserAdmin = false;
    }
  }

  // Fetch current teacher's performance data automatically
  async function fetchTeacherPerformance() {
    loading = true;
    submitError = null;

    const token = localStorage.getItem('jwtToken');
    if (!token) {
      submitError = 'JWT token not found. Please log in.';
      loading = false;
      return;
    }

    if (!selectedMonth) {
      submitError = 'Please select a month first.';
      loading = false;
      return;
    }

    // Create a timeout promise to prevent infinite loading
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timeout')), 30000); // 30 second timeout
    });

    try {
      // Get current teacher info first with timeout protection
      const teacherResponsePromise = fetch('/studentplayground/api/get_current_teacher_info/', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const teacherResponse = await Promise.race([teacherResponsePromise, timeoutPromise]);

      if (!teacherResponse.ok) {
        const errorText = await teacherResponse.text();
        throw new Error(`Failed to get teacher information: ${teacherResponse.status} - ${errorText}`);
      }

      const teacherData = await teacherResponse.json();
      teacherInfo = teacherData;

      // Validate teacher data before proceeding
      if (!teacherData.teacher_id || !teacherData.teacher_name) {
        throw new Error('Invalid teacher information received');
      }

      // Now get performance data with timeout protection
      const performanceResponsePromise = fetch('/studentplayground/api/teacher_performance_rating/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          teacher_id: teacherData.teacher_id,
          teacher_name: teacherData.teacher_name,
          selected_month: selectedMonth,
          selected_at: new Date().toISOString()
        })
      });

      const response = await Promise.race([performanceResponsePromise, timeoutPromise]);

      if (response.ok) {
        const data = await response.json();
        console.log('Teacher performance data received:', data);
        retentionData = data.retention_analysis;
        commentsData = data.comments_analysis;
        completionData = data.completion_analysis;
        surveyData = data.survey_analysis;
        paymentData = data.payment_analysis;
        bonusData = data.bonus_analysis;
      } else {
        const errorData = await response.json().catch(() => ({}));
        submitError = errorData.error || `Failed to load performance data: ${response.status}`;
        // Still show teacher info even if performance data fails
        console.log('Performance data failed but teacher info available');
      }
    } catch (error) {
      console.error('Error fetching teacher performance:', error);
      if (error.message === 'Request timeout') {
        submitError = 'Request timed out. Please check your connection and try again.';
      } else {
        submitError = `Network error: ${error.message}`;
      }
      // Make sure navigation still works even if there are errors
      console.log('Error occurred but ensuring component remains navigable');
    } finally {
      // Ensure loading is always set to false and navigation remains functional
      if (mounted) {
        loading = false;
      }
    }
  }

  // On mount, automatically fetch teacher performance
  onMount(() => {
    // Initialize available months
    availableMonths = generateTeacherPerformanceMonths();
    
    // Set default to current month
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // getMonth() is 0-indexed
    const currentMonthValue = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
    
    // Check if current month is in available months, otherwise use first available month
    const isCurrentMonthAvailable = availableMonths.some(m => m.value === currentMonthValue);
    selectedMonth = isCurrentMonthAvailable ? currentMonthValue : (availableMonths.length > 0 ? availableMonths[0].value : '');
    
    // Add a small delay to ensure the component is fully mounted before making API calls
    setTimeout(() => {
      if (mounted) {
        checkIfUserIsAdmin();
        fetchTeacherPerformance();
      }
    }, 100);

    // Safety mechanism: Clear loading state after a maximum time
    const maxLoadingTime = setTimeout(() => {
      if (mounted && loading) {
        console.warn('Loading state cleared due to timeout');
        loading = false;
        if (!submitError) {
          submitError = 'Data loading timed out. Please try again.';
        }
      }
    }, 60000); // 60 seconds max loading time

    // Clear the timeout when component is destroyed
    return () => {
      clearTimeout(maxLoadingTime);
    };
  });

  // Cleanup on destroy - delay the cleanup to ensure navigation isn't interfered with
  onDestroy(() => {
    // Use a small delay to prevent interference with navigation
    setTimeout(() => {
      mounted = false;
    }, 50);
  });
</script>

<NavBarReusable label="Statistika" />

<div class="teacher-performance-page">
  <h2>Statistika</h2>
  <p class="description">Čia pateikti jūsų mokymo veiklos rodikliai ir mokinių atsiliepimai.</p>

  <div class="month-selection">
    <label for="month-select"><strong>Pasirinkite mėnesį:</strong></label>
    <select
      id="month-select"
      bind:value={selectedMonth}
      class="month-dropdown"
      on:change={fetchTeacherPerformance}
    >
      <option value="">Pasirinkite mėnesį...</option>
      {#each availableMonths as month}
        <option value={month.value}>{month.label}</option>
      {/each}
    </select>
  </div>

  {#if loading}
    <div class="loading-message">
      Kraunami jūsų veiklos duomenys...
    </div>
  {/if}

  {#if submitError}
    <div class="error-message">
      {submitError}
      <div class="error-actions">
        <button class="retry-button" on:click={fetchTeacherPerformance}>
          Bandyti dar kartą
        </button>
      </div>
    </div>
  {/if}

  <!-- Always show basic content even when there's no data -->
  {#if !loading && !submitError && (!retentionData && !commentsData && !completionData && !surveyData && !paymentData && !bonusData)}
    <div class="no-data-message">
      <p>Šiame mėnesyje pamokų nerasta</p>
    </div>
  {/if}

  {#if teacherInfo && !loading}
    <div class="teacher-info">
      <h3>Teacher Information</h3>
      <p><strong>Name:</strong> {teacherInfo.teacher_name}</p>
      {#if teacherInfo.email}
        <p><strong>Email:</strong> {teacherInfo.email}</p>
      {/if}
    </div>
  {/if}

  <!-- Bonus Criteria Summary Section -->
  {#if bonusData}
    <div class="bonus-criteria-summary">
      <h3>🎯 Bonus Criteria Status</h3>
      <div class="criteria-grid">
        <div class="criterion-card {bonusData.details.admin_punctuality_rate >= 90 ? 'eligible' : 'not-eligible'}">
          <div class="criterion-icon">
            {#if bonusData.details.admin_punctuality_rate >= 90}✅{:else}❌{/if}
          </div>
          <div class="criterion-content">
            <h4>Profesionalumas</h4>
            <p class="criterion-value">{bonusData.details.admin_punctuality_rate ?? 'nerasta'}% (90%+ reikia)</p>
            <p class="criterion-status {bonusData.details.admin_punctuality_rate >= 90 ? 'met' : 'not-met'}">
              {bonusData.details.admin_punctuality_rate >= 90 ? 'Įvykdyta' : 'Neįvykdyta'}
            </p>
            <small>Incidentų: {bonusData.details.teacher_incidents_count ?? 'nerasta'}</small>
          </div>
        </div>

        <div class="criterion-card {bonusData.details.teacher_comment_submission_rate >= 93 ? 'eligible' : 'not-eligible'}">
          <div class="criterion-icon">
            {#if bonusData.details.teacher_comment_submission_rate >= 93}✅{:else}❌{/if}
          </div>
          <div class="criterion-content">
            <h4>Komentarų pateikimas</h4>
            <p class="criterion-value">{bonusData.details.teacher_comment_submission_rate ?? 'nerasta'}% (93% reikia)</p>
            <p class="criterion-status {bonusData.details.teacher_comment_submission_rate >= 93 ? 'met' : 'not-met'}">
              {bonusData.details.teacher_comment_submission_rate >= 93 ? 'Įvykdyta' : 'Neįvykdyta'}
            </p>
            <small>Komentarų: {bonusData.details.classes_with_teacher_comments ?? 'nerasta'}/{bonusData.details.total_classes ?? 'nerasta'}</small>
          </div>
        </div>
      </div>

      {#if bonusData.eligible_for_bonus}
        <div class="bonus-eligibility-banner">
          <div class="bonus-icon">🎉</div>
          <div class="bonus-message">
            <h4>Sveikiname! Gaunate premijos priedą</h4>
            <p>Kiekviena mokama pamoka šį mėnesį gauna <strong>+€{bonusData.amount_per_class ?? 2} premijos priedą</strong></p>
            {#if bonusData.details.month_period}
              <small>Laikotarpis: {new Date(bonusData.details.month_period.start_date).toLocaleDateString()} - {new Date(bonusData.details.month_period.end_date).toLocaleDateString()}</small>
            {/if}
          </div>
        </div>
      {:else}
        <div class="bonus-eligibility-banner not-eligible">
          <div class="bonus-icon">📊</div>
          <div class="bonus-message">
            <h4>Premijos kriterijai neįvykdyti</h4>
            <p>Kai visi kriterijai bus įvykdyti, gausite <strong>+€{bonusData.amount_per_class ?? 2} premijos priedą</strong> už kiekvieną mokamą pamoką</p>
            {#if bonusData.details.month_period}
              <small>Laikotarpis: {new Date(bonusData.details.month_period.start_date).toLocaleDateString()} - {new Date(bonusData.details.month_period.end_date).toLocaleDateString()}</small>
            {/if}
          </div>
        </div>
      {/if}
    </div>
  {/if}

  {#if paymentData}
    <div class="retention-container">
      <h3>Mano mokėjimų analizė</h3>
      
      <div class="retention-summary">
        <div class="retention-rate">
          <strong>Bendras mokėjimas: €{paymentData.payment_analysis.total_payment}</strong>
        </div>
        <p>({paymentData.payment_analysis.total_completed_classes} baigta, {paymentData.payment_analysis.total_upcoming_classes} planuojama | Vidutiniškai €{paymentData.payment_analysis.average_payment_per_class} už pamoką)</p>
        <p>Laikotarpis: {new Date(paymentData.period.start_date).toLocaleDateString()} - {new Date(paymentData.period.end_date).toLocaleDateString()}</p>
      </div>

      {#if paymentData.payment_analysis.total_month_classes > 0}
        <!-- Class Summary Section -->
        <div class="comments-section">
          <h4>Pamokų suvestinė</h4>
          <div class="payment-breakdown-container">
            <div class="payment-section">
              <h5>Pamokų tipai</h5>
              <ul class="class-count-list">
                <li><strong>Individualios pamokos:</strong> {paymentData.payment_analysis.payment_summary?.individual_classes?.count || 0} pamokos</li>
                <li><strong>Porų pamokos:</strong> {paymentData.payment_analysis.payment_summary?.pair_classes?.count || 0} pamokos</li>
                <li><strong>Grupės pamokos:</strong> {paymentData.payment_analysis.payment_summary?.group_classes?.count || 0} pamokos</li>
              </ul>
            </div>
            
            <div class="payment-section">
              <h5>Specialios kategorijos</h5>
              <ul class="class-count-list">
                <li><strong>Specialių sąlygų pamokos:</strong> {paymentData.payment_analysis.payment_summary?.ib_classes?.count || 0} pamokos</li>
                {#if (paymentData.payment_analysis.payment_summary?.free_classes?.count || 0) > 0}
                  <li><strong>Nemokamos pamokos:</strong> {paymentData.payment_analysis.payment_summary?.free_classes?.count || 0} pamokos</li>
                {/if}
                <li class="total-classes"><strong>Iš viso pamokų: {paymentData.payment_analysis.total_completed_classes + paymentData.payment_analysis.total_upcoming_classes}</strong></li>
              </ul>
            </div>
          </div>
          <div class="payment-total-only">
            <p><strong>Bendras mokėjimas: €{paymentData.payment_analysis.total_payment}</strong></p>
          </div>
        </div>

        <!-- Class-by-Class Payment Details -->
        <div class="comments-section">
          <h4>Detalūs mokėjimų duomenys pagal pamokas</h4>
          <div class="attendance-details">
            {#each paymentData.payment_analysis.class_payment_details
              .sort((a, b) => new Date(a.class_time) - new Date(b.class_time))
              .slice(0, showAllPaymentClasses ? paymentData.payment_analysis.class_payment_details.length : 10) as classDetail}
              <div class="attendance-item {classDetail.status === 'upcoming' ? 'upcoming-class' : ''}">
                <div class="attendance-header">
                  <strong>{classDetail.class_name}</strong>
                  <div class="class-status-payment">
                    <span class="class-status {classDetail.status}">{classDetail.status === 'upcoming' ? 'planuojama' : classDetail.status === 'completed' ? 'baigta' : classDetail.status}</span>
                    <span class="attendance-rate {classDetail.is_free_class ? 'free-class' : 'paid-class'}">
                      €{classDetail.payment}
                    </span>
                  </div>
                </div>
                <div class="attendance-stats">
                  <small>{classDetail.display_time}</small>
                  <small>Mokiniai: {classDetail.students} ({classDetail.student_count_category === 'individual' ? 'individualus' : classDetail.student_count_category === 'pair' ? 'pora' : classDetail.student_count_category === 'group' ? 'grupė' : classDetail.student_count_category})</small>
                  {#if classDetail.special_conditions && classDetail.special_conditions.length > 0}
                    <div class="special-conditions-container">
                      {#each classDetail.special_conditions as condition}
                        {#if condition === 'IB'}
                          <small class="special-condition ib-condition">IB (+€5)</small>
                        {:else if condition === 'Special Needs'}
                          <small class="special-condition special-needs-condition">Spec. poreikiai (+€3)</small>
                        {:else if condition === 'Lithuanian Second Language'}
                          <small class="special-condition lithuanian-condition">Lietuvių K2(+€3)</small>
                        {/if}
                      {/each}
                      {#if classDetail.special_conditions_surcharge > 0}
                        <small class="total-surcharge">Viso priedas: +€{classDetail.special_conditions_surcharge}</small>
                      {/if}
                    </div>
                  {/if}
                  {#if classDetail.bonus_applied}
                    <div class="bonus-container">
                      <small class="special-condition bonus-condition">PREMIJA (+€{classDetail.bonus_amount_applied ?? bonusData?.amount_per_class ?? 2})</small>
                    </div>
                  {/if}
                  {#if classDetail.is_free_class}
                    <small class="free-reason">Nemokai: Pirmoji pamoka nepasilikusiems mokiniams</small>
                  {/if}
                  {#if classDetail.has_first_time_students && classDetail.first_time_students.length > 0}
                    <small class="first-time">Pirmą kartą: {classDetail.first_time_students.join(', ')}</small>
                  {/if}
                  {#if classDetail.has_retained_students && classDetail.retained_students.length > 0}
                    <small class="retained">Pasilieka: {classDetail.retained_students.join(', ')}</small>
                  {/if}
                </div>
              </div>
            {/each}
            {#if paymentData.payment_analysis.class_payment_details.length > 10 && !showAllPaymentClasses}
              <div class="show-all-button-container">
                <button class="show-all-button" on:click={() => showAllPaymentClasses = true}>
                  Peržiūrėti visas pamokas ({paymentData.payment_analysis.class_payment_details.length - 10} daugiau)
                </button>
              </div>
            {/if}
          </div>
        </div>
      {:else}
        <div class="no-data">
          <p>Šį mėnesį pamokų nerasta.</p>
        </div>
      {/if}
    </div>
  {/if}



  {#if surveyData}
    <div class="retention-container">
      <h3>Mano apklausų pateikimo dažnis</h3>
      
      <div class="retention-summary">
        <div class="retention-rate">
          <strong>Apklausų pateikimo dažnis: {surveyData.survey_analysis.submission_rate_percentage}%</strong>
        </div>
        <p>({surveyData.survey_analysis.surveys_submitted} iš {surveyData.survey_analysis.total_classes} pamokų turi mokytojų apklausas)</p>
        <p>Laikotarpis: {new Date(surveyData.month_period.start_date).toLocaleDateString()} - {new Date(surveyData.month_period.end_date).toLocaleDateString()}</p>
      </div>

      {#if surveyData.survey_analysis.total_classes > 0}
        <!-- Progress Bar -->
        <div class="comments-section">
          <h4>Pateikimo apžvalga</h4>
          <div class="metric-item">
            <strong>Bendras atitikimas</strong>
            <div class="metric-bar">
              <div class="metric-fill" style="width: {surveyData.survey_analysis.submission_rate_percentage}%"></div>
            </div>
            <small>{surveyData.survey_analysis.surveys_submitted} pateikta, {surveyData.survey_analysis.classes_missing_surveys.length} trūksta</small>
          </div>
        </div>

        <!-- Classes with Surveys -->
        {#if surveyData.survey_analysis.classes_with_surveys.length > 0}
          <div class="comments-section">
            <h4>Pamokos su mokytojų apklausomis ({surveyData.survey_analysis.classes_with_surveys.length})</h4>
            <div class="sample-comments">
              {#each surveyData.survey_analysis.classes_with_surveys.slice(0, 5) as classItem}
                <div class="comment-item">
                  <strong>{classItem.class_name}</strong>
                  <div class="class-details">
                    <small>{new Date(classItem.class_time).toLocaleDateString()} - {classItem.class_display_time}</small>
                    <small>Mokiniai: {classItem.students}</small>
                    {#if classItem.note_text}
                      <p>"{classItem.note_text}"</p>
                    {/if}
                    <div class="survey-ratings">
                      {#if classItem.effort_rating > 0}
                        <span>Pastangos: {classItem.effort_rating}/5</span>
                      {/if}
                      {#if classItem.learned_rating > 0}
                        <span>Mokymasis: {classItem.learned_rating}/5</span>
                      {/if}
                      {#if classItem.general_rating > 0}
                        <span>Bendrai: {classItem.general_rating}/5</span>
                      {/if}
                    </div>
                  </div>
                </div>
              {/each}
              {#if surveyData.survey_analysis.classes_with_surveys.length > 5}
                <div class="more-classes">
                  <small>... ir dar {surveyData.survey_analysis.classes_with_surveys.length - 5} pamokų su apklausomis</small>
                </div>
              {/if}
            </div>
          </div>
        {/if}

        <!-- Classes Missing Surveys -->
        {#if surveyData.survey_analysis.classes_missing_surveys.length > 0}
          <div class="comments-section">
            <h4>Pamokos be mokytojų apklausų ({surveyData.survey_analysis.classes_missing_surveys.length})</h4>
            <div class="missing-surveys">
              {#each surveyData.survey_analysis.classes_missing_surveys as classItem}
                <div class="missing-item">
                  <strong>{classItem.class_name}</strong>
                  <div class="class-details">
                    <small>{new Date(classItem.class_time).toLocaleDateString()} - {classItem.class_display_time}</small>
                    <small>Mokiniai: {classItem.students}</small>
                    <span class="missing-badge">Trūksta apklausos</span>
                  </div>
                </div>
              {/each}
            </div>
          </div>
        {/if}
      {:else}
        <div class="no-data">
          <p>Šį mėnesį pamokų nerasta.</p>
        </div>
      {/if}
    </div>
  {/if}

  {#if retentionData}
    <div class="retention-container">
      <h3>Mano mokinių išlikimo analizė</h3>
      
      <div class="retention-summary">
        <div class="retention-rate">
          <strong>Išlikimo dažnis: {retentionData.retention.retention_rate_percentage}%</strong>
        </div>
        <p>({retentionData.retention.retained_students} iš {retentionData.current_month.unique_students} mokinių tęsia kito mėnesio pamokas)</p>
      </div>

      <div class="month-comparison">
        <div class="month-data">
          <h4>Einamasis mėnuo</h4>
          <ul>
            <li><strong>Unikalūs mokiniai:</strong> {retentionData.current_month.unique_students}</li>
            <li><strong>Iš viso pamokų:</strong> {retentionData.current_month.total_classes}</li>
            <li><strong>Laikotarpis:</strong> {new Date(retentionData.current_month.start_date).toLocaleDateString()} - {new Date(retentionData.current_month.end_date).toLocaleDateString()}</li>
          </ul>
        </div>
        
        <div class="month-data">
          <h4>Kitas mėnuo</h4>
          <ul>
            <li><strong>Unikalūs mokiniai:</strong> {retentionData.next_month.unique_students}</li>
            <li><strong>Iš viso pamokų:</strong> {retentionData.next_month.total_classes}</li>
            <li><strong>Laikotarpis:</strong> {new Date(retentionData.next_month.start_date).toLocaleDateString()} - {new Date(retentionData.next_month.end_date).toLocaleDateString()}</li>
          </ul>
        </div>
      </div>

      {#if retentionData.retention.retained_students_details.length > 0}
        <div class="student-details">
          <h4>Pasiliekantys mokiniai ({retentionData.retention.retained_students})</h4>
          <ul class="student-list">
            {#each retentionData.retention.retained_students_details as student}
              <li>{student.name || student.username}</li>
            {/each}
          </ul>
        </div>
      {/if}

      {#if retentionData.retention.lost_students_details.length > 0}
        <div class="student-details">
          <h4>Netęsiantys mokiniai ({retentionData.retention.lost_students_details.length})</h4>
          <ul class="student-list">
            {#each retentionData.retention.lost_students_details as student}
              <li>{student.name || student.username}</li>
            {/each}
          </ul>
        </div>
      {/if}
    </div>
  {/if}

  {#if commentsData}
    <div class="retention-container">
      <h3>Mokinių atsiliepimai apie mano pamokas</h3>
      
      <div class="retention-summary">
        <div class="retention-rate">
          <strong>Komentarai: {commentsData.comments_analysis.total_comments}</strong>
        </div>
        <p>Mokinių atsiliepimai iš {new Date(commentsData.month_period.start_date).toLocaleDateString()} - {new Date(commentsData.month_period.end_date).toLocaleDateString()}</p>
      </div>

      {#if commentsData.comments_analysis.total_comments > 0}
        <!-- Student Rating Average Section -->
        <div class="comments-section">
          <h4>Mokinių įvertinimas</h4>
          <div class="student-rating-summary">
            <div class="rating-display">
              <div class="rating-value">
                <strong>{commentsData.comments_analysis.rating_averages?.general?.average || 0}/5</strong>
              </div>
              <div class="rating-details">
                <p>Vidutinis pamokų įvertinimas</p>
                <p>({commentsData.comments_analysis.rating_averages?.general?.rated_comments || 0} mokinių įvertinimai)</p>
              </div>
            </div>
            <div class="rating-bar-container">
              <div class="metric-bar">
                <div class="metric-fill" style="width: {((commentsData.comments_analysis.rating_averages?.general?.average || 0) / 5) * 100}%"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sample Comments Section - Only visible to admins -->
        {#if isUserAdmin && commentsData.comments_analysis.sample_comments.length > 0}
          <div class="comments-section">
            <h4>Naujausi mokinių komentarai</h4>
            <div class="sample-comments">
              {#each commentsData.comments_analysis.sample_comments as comment}
                <div class="comment-item">
                  <strong>{comment.student}:</strong>
                  <span>"{comment.text}"</span>
                </div>
              {/each}
            </div>
          </div>
        {/if}
      {:else}
        <div class="no-data">
          <p>Šį mėnesį mokinių komentarų nerasta.</p>
        </div>
      {/if}
    </div>
  {/if}

  {#if completionData}
    <div class="retention-container">
      <h3>Mano pamokų baigimas ir punktualumas</h3>
      
      <div class="retention-summary">
        <div class="retention-rate">
          <strong>Veiklos efektyvumas: {completionData.completion_analysis.operational_efficiency?.overall_score || 0}%</strong>
        </div>
        <p>Bendras baigimo ir punktualumo įvertinimas už {new Date(completionData.month_period.start_date).toLocaleDateString()} - {new Date(completionData.month_period.end_date).toLocaleDateString()}</p>
      </div>

      {#if completionData.completion_analysis.total_scheduled > 0}
        <!-- Main Metrics Section -->
        <div class="month-comparison">
          <div class="month-data">
            <h4>Mano veikla</h4>
            <ul>
              <li><strong>Viso suplanuota:</strong> {completionData.completion_analysis.total_scheduled}</li>
              <li><strong>Viso baigta:</strong> {completionData.completion_analysis.teacher_completed_classes}</li>
              <li><strong>Viso atšaukta:</strong> {completionData.completion_analysis.teacher_cancelled_classes}</li>
              <li><strong>Įvykusios Pamokos:</strong> {completionData.completion_analysis.teacher_completion_rate_percentage}%</li>
            </ul>
          </div>
          
          <div class="month-data">
            <h4>Mokėjimas ir atsakomybė</h4>
            <ul>
              <li><strong>Apmokamos pamokos:</strong> {completionData.completion_analysis.payment_eligible_classes?.total_eligible || 0}</li>
              <li><strong>Mokinių atšaukimai:</strong> {completionData.completion_analysis.responsibility_breakdown?.student_cancellations || 0}</li>
              <li><strong>Įvykusios pamokos:</strong> {completionData.completion_analysis.classes_actually_held}</li>
              <li><strong>Tikrasis baigimo dažnis:</strong> {completionData.completion_analysis.actual_completion_rate_percentage}%</li>
            </ul>
          </div>
          
          <div class="month-data">
            <h4>Mano punktualumas</h4>
            <ul>
              <li><strong>Pamokos laiku:</strong> {completionData.completion_analysis.punctuality?.on_time_classes || 0}</li>
              <li><strong>Viso vėlavimo atvejų:</strong> {completionData.completion_analysis.punctuality?.total_late_incidents || 0}</li>
              <!-- <li><strong>Oficialus vėlavimas:</strong> {completionData.completion_analysis.punctuality.formal_late_incidents}</li> -->
              <!-- <li><strong>Mokinių praneštas vėlavimas:</strong> {completionData.completion_analysis.punctuality.student_reported_late}</li> -->
              <li><strong>Punktualumo dažnis:</strong> {completionData.completion_analysis.punctuality?.punctuality_rate_percentage || 0}%</li>
            </ul>
          </div>
        </div>

        <!-- Student Attendance Section -->
        <div class="comments-section">
          <h4>Mokinių dalyvavimas mano pamokose</h4>
          <div class="month-comparison">
            <div class="month-data">
              <h5>Dalyvavimo suvestinė</h5>
              <ul>
                              <li><strong>Pamokos su mokiniais:</strong> {completionData.completion_analysis.student_attendance?.total_classes_with_students || 0}</li>
              <li><strong>Pamokos su dalyviais:</strong> {completionData.completion_analysis.student_attendance?.classes_with_attendance || 0}</li>
              <li><strong>Pamokos be dalyvių:</strong> {completionData.completion_analysis.student_attendance?.classes_with_no_attendance || 0}</li>
              </ul>
            </div>
            
            <div class="month-data">
              <h5>Dalyvavimo rodikliai</h5>
              <ul>
                <li><strong>Mokinių dalyvavimo dažnis:</strong> {completionData.completion_analysis.student_attendance?.student_attendance_rate_percentage || 0}%</li>
                <li><strong>Nedalyvavimo dažnis:</strong> {completionData.completion_analysis.student_attendance?.student_no_show_rate || 0}%</li>
                <li><strong>Viso dalyvavę:</strong> {completionData.completion_analysis.student_attendance?.total_actual_attendances || 0}/{completionData.completion_analysis.student_attendance?.total_expected_attendances || 0}</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Visual Progress Bars -->
        <div class="comments-section">
          <h4>Veiklos apžvalga</h4>
          <div class="metrics-grid">
            <div class="metric-item">
              <strong>Įvykusios Pamokos</strong>
              <div class="metric-bar">
                <div class="metric-fill" style="width: {completionData.completion_analysis.teacher_completion_rate_percentage}%"></div>
              </div>
              <small>{completionData.completion_analysis.teacher_completed_classes} iš {completionData.completion_analysis.total_scheduled} pamokų įvykusios</small>
            </div>
            
            <div class="metric-item">
              <strong>Mano punktualumo dažnis</strong>
              <div class="metric-bar">
                <div class="metric-fill" style="width: {completionData.completion_analysis.punctuality?.punctuality_rate_percentage || 0}%"></div>
              </div>
              <small>{completionData.completion_analysis.punctuality?.on_time_classes || 0} iš {completionData.completion_analysis.total_scheduled} pamokų laiku ({completionData.completion_analysis.punctuality?.total_late_incidents || 0} vėlavimo atvejų)</small>
            </div>
            
            <div class="metric-item">
              <strong>Mokinių dalyvavimo dažnis</strong>
              <div class="metric-bar">
                <div class="metric-fill" style="width: {completionData.completion_analysis.student_attendance?.student_attendance_rate_percentage || 0}%"></div>
              </div>
              <small>{completionData.completion_analysis.student_attendance?.total_actual_attendances || 0} iš {completionData.completion_analysis.student_attendance?.total_expected_attendances || 0} dalyvautų pamokų</small>
            </div>
          </div>
        </div>

        <!-- Student Attendance Details -->
        <!-- Temporarily commented out student attendance details display
        {#if completionData.completion_analysis.student_attendance_details && completionData.completion_analysis.student_attendance_details.length > 0}
          <div class="comments-section">
            <h4>Detalus dalyvavimas pagal pamokas</h4>
            <div class="attendance-details">
              {#each completionData.completion_analysis.student_attendance_details.slice(0, 10) as classDetail}
                <div class="attendance-item">
                  <div class="attendance-header">
                    <strong>{classDetail.class_name}</strong>
                    <span class="attendance-rate">{classDetail.attendance_rate.toFixed(1)}%</span>
                  </div>
                  <div class="attendance-stats">
                    <small>{classDetail.display_time}</small>
                    <small>{classDetail.attending_students}/{classDetail.enrolled_students} mokinių dalyvavo</small>
                    {#if classDetail.no_show_students > 0}
                      <small class="no-show">({classDetail.no_show_students} nedalyvavo)</small>
                    {/if}
                  </div>
                </div>
              {/each}
              {#if completionData.completion_analysis.student_attendance_details.length > 10}
                <div class="more-classes">
                  <small>... ir dar {completionData.completion_analysis.student_attendance_details.length - 10} pamokų</small>
                </div>
              {/if}
            </div>
          </div>
        {/if}
        -->

        <!-- Weekly Trends if available -->
        {#if Object.keys(completionData.completion_analysis.weekly_trends || {}).length > 0}
          <div class="comments-section">
            <h4>Savaitės tendencijos</h4>
            <div class="weekly-trends">
              {#each Object.entries(completionData.completion_analysis.weekly_trends) as [week, data]}
                <div class="week-item">
                  <strong>Savaitė {week.split('_')[1]}</strong>
                  <div class="week-stats">
                    <span>Baigimas: {data.completion_rate.toFixed(1)}%</span>
                    <span>Punktualumas: {data.punctuality_rate.toFixed(1)}%</span>
                    {#if data.total_late_incidents > 0}
                      <span class="late-incidents">Vėluoja: {data.formal_late || 0} oficialiai</span>
                    {/if}
                  </div>
                  <small>{data.period}</small>
                </div>
              {/each}
            </div>
          </div>
        {/if}

        <!-- Incident Details if any exist -->
        {#if (completionData.completion_analysis.incident_summary?.total_teacher_incidents || 0) > 0}
          <div class="comments-section">
            <h4>Incidentų suvestinė</h4>
            <div class="incident-summary">
              <div class="responsibility-breakdown">
                <div class="teacher-incidents">
                  <p><strong>Mano incidentai:</strong> {completionData.completion_analysis.incident_summary?.total_teacher_incidents || 0}</p>
                  <ul>
                    <li>Atšaukimai: {completionData.completion_analysis.incident_summary?.teacher_cancellations || 0}</li>
                    <li>Vėlavimas: {completionData.completion_analysis.incident_summary?.teacher_late_arrivals || 0}</li>
                    <li>Kitos problemos: {completionData.completion_analysis.incident_summary?.teacher_other_issues || 0}</li>
                  </ul>
                </div>
                
                <div class="student-incidents">
                  <p><strong>Mokinių incidentai:</strong> {completionData.completion_analysis.incident_summary?.total_student_incidents || 0}</p>
                  <ul>
                    <li>Atšaukimai: {completionData.completion_analysis.incident_summary?.student_cancellations || 0} <em>(vis dar apmokama)</em></li>
                    <!-- <li>Vėlavimas: {completionData.completion_analysis.incident_summary.student_late_arrivals}</li> -->
                    <li>Kitos problemos: {completionData.completion_analysis.incident_summary?.student_other_issues || 0}</li>
                  </ul>
                </div>
              </div>
              
              {#if (completionData.completion_analysis.incident_details?.teacher_cancellations?.length || 0) > 0}
                <div class="incident-details">
                  <h5>Mano atšaukimai:</h5>
                  {#each completionData.completion_analysis.incident_details.teacher_cancellations.slice(0, 3) as incident}
                    <div class="incident-item">
                      <strong>{incident.class_name}</strong> - {new Date(incident.class_time).toLocaleDateString()}
                      <small>Mokinys: {incident.student}</small>
                      {#if incident.comment !== 'No comment provided'}
                        <span>Komentaras: {incident.comment}</span>
                      {/if}
                    </div>
                  {/each}
                </div>
              {/if}

              {#if (completionData.completion_analysis.incident_details?.student_cancellations?.length || 0) > 0}
                <div class="incident-details">
                  <h5>Mokinių atšaukimai (vis dar apmokama):</h5>
                  {#each completionData.completion_analysis.incident_details.student_cancellations.slice(0, 3) as incident}
                    <div class="incident-item student-cancellation">
                      <strong>{incident.class_name}</strong> - {new Date(incident.class_time).toLocaleDateString()}
                      <small>Mokinys: {incident.student}</small>
                      {#if incident.comment !== 'No comment provided'}
                        <span>Komentaras: {incident.comment}</span>
                      {/if}
                      <span class="payment-note">💰 Tinkama apmokėjimui</span>
                    </div>
                  {/each}
                </div>
              {/if}

              {#if (completionData.completion_analysis.incident_details?.teacher_late_arrivals?.length || 0) > 0}
                <div class="incident-details">
                  <h5>Mano vėlavimai:</h5>
                  {#each completionData.completion_analysis.incident_details.teacher_late_arrivals.slice(0, 3) as incident}
                    <div class="incident-item">
                      <strong>{incident.class_name}</strong> - {new Date(incident.class_time).toLocaleDateString()}
                      <small>Mokinys: {incident.student}</small>
                      {#if incident.comment !== 'No comment provided'}
                        <span>Komentaras: {incident.comment}</span>
                      {/if}
                    </div>
                  {/each}
                </div>
              {/if}

              <!-- Temporarily commented out student late details display
              {#if completionData.completion_analysis.punctuality.student_late_details.length > 0}
                <div class="incident-details">
                  <h5>Mokinių praneštos punktualumo problemos:</h5>
                  {#each completionData.completion_analysis.punctuality.student_late_details.slice(0, 5) as report}
                    <div class="incident-item student-feedback">
                      <strong>Pamoka: {report.class_id}</strong>
                      <small>Pranešė: {report.student_name}</small>
                      {#if report.note_text !== 'No additional comment'}
                        <span>Mokinio komentaras: "{report.note_text}"</span>
                      {/if}
                      <div class="rating-info">
                        {#if report.effort_rating > 0}
                          <small>Pastangų įvertinimas: {report.effort_rating}/5</small>
                        {/if}
                        {#if report.learned_rating > 0}
                          <small>Mokymosi įvertinimas: {report.learned_rating}/5</small>
                        {/if}
                        {#if report.general_rating > 0}
                          <small>Bendras įvertinimas: {report.general_rating}/5</small>
                        {/if}
                        <small class="punctuality-issue">Laiku: Ne</small>
                      </div>
                    </div>
                  {/each}
                  {#if completionData.completion_analysis.punctuality.student_late_details.length > 5}
                    <div class="more-reports">
                      <small>... ir dar {completionData.completion_analysis.punctuality.student_late_details.length - 5} mokinių pranešimų</small>
                    </div>
                  {/if}
                </div>
              {/if}
              -->
            </div>
          </div>
        {:else}
          <div class="no-incidents">
            <p>✅ Šį mėnesį incidentų nepranešta!</p>
          </div>
        {/if}
      {:else}
        <div class="no-data">
          <p>Šį mėnesį suplanuotų pamokų nerasta.</p>
        </div>
      {/if}
    </div>
  {/if}

</div>

<style>
  .teacher-performance-page {
    max-width: 800px;
    margin: 0 auto;
    padding: 2em;
  }

  .teacher-performance-page h2 {
    color: #fab45a;
    margin-bottom: 0.5em;
    text-align: center;
  }

  .description {
    color: #666;
    margin-bottom: 2em;
    font-size: 1.1em;
    text-align: center;
  }

  .month-selection {
    margin-bottom: 2em;
    text-align: center;
  }

  .month-selection label {
    display: block;
    margin-bottom: 0.5em;
    font-weight: bold;
    color: #333;
  }

  .month-dropdown {
    width: 100%;
    max-width: 300px;
    padding: 0.75em;
    border: 2px solid #ddd;
    border-radius: 8px;
    font-size: 1em;
    background-color: white;
    cursor: pointer;
    transition: border-color 0.2s;
    box-sizing: border-box;
  }

  .month-dropdown:focus {
    outline: none;
    border-color: #fab45a;
    box-shadow: 0 0 0 3px rgba(250, 180, 90, 0.1);
  }

  .month-dropdown:hover {
    border-color: #fab45a;
  }

  .loading-message {
    text-align: center;
    padding: 2em;
    color: #fab45a;
    font-size: 1.2em;
  }

  .teacher-info {
    background: #f8f9fa;
    border: 2px solid #fab45a;
    border-radius: 8px;
    padding: 1.5em;
    margin-bottom: 2em;
  }

  .teacher-info h3 {
    margin: 0 0 1em 0;
    color: #fab45a;
  }

  .teacher-info p {
    margin: 0.5em 0;
    font-size: 1em;
  }

  .error-message {
    color: #d32f2f;
    background: #ffebee;
    padding: 1em;
    border-radius: 6px;
    border-left: 4px solid #d32f2f;
    margin-top: 1em;
  }

  .error-actions {
    margin-top: 1em;
    text-align: center;
  }

  .retry-button {
    background-color: #fab45a;
    color: black;
    border: none;
    padding: 10px 20px;
    border-radius: 6px;
    font-size: 0.9em;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.3s ease;
  }

  .retry-button:hover {
    background-color: #e09a43;
  }

  /* All the styles from TeacherPerformanceRating.svelte for data display */
  .retention-container {
    margin-top: 2em;
    padding: 15px;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
    color: black;
  }

  .retention-container h3 {
    margin: 0 0 15px 0;
    color: #333;
    font-size: 1.2em;
  }

  .retention-summary {
    margin-bottom: 20px;
    padding: 10px;
    background-color: #f8f9fa;
    border-radius: 6px;
    text-align: center;
  }

  .retention-rate {
    font-size: 1.3em;
    color: #fab45a;
    margin-bottom: 5px;
  }

  .retention-summary p {
    margin: 0;
    color: #666;
    font-size: 0.9em;
  }

  .month-comparison {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 20px;
  }

  /* Payment breakdown specific styling */
  .payment-breakdown-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 20px;
  }

  .payment-section {
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 6px;
  }

  .payment-section h5 {
    margin: 0 0 12px 0;
    color: #fab45a;
    font-size: 1em;
    border-bottom: 1px solid #ddd;
    padding-bottom: 4px;
  }

  .class-count-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .class-count-list li {
    padding: 6px 0;
    font-size: 0.9em;
    border-bottom: 1px solid #e9ecef;
  }

  .class-count-list li:last-child {
    border-bottom: none;
  }

  .total-classes {
    background-color: #e8f5e8;
    color: #2e7d32;
    padding: 6px 8px;
    border-radius: 4px;
    margin-top: 6px;
    border: none !important;
  }

  .payment-total-only {
    background-color: #fab45a;
    color: black;
    padding: 12px;
    border-radius: 8px;
    margin-top: 20px;
    text-align: center;
  }

  .payment-total-only p {
    margin: 0;
    font-size: 1.1em;
  }

  .month-data {
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 6px;
  }

  .month-data h4 {
    margin: 0 0 10px 0;
    color: #333;
    font-size: 1em;
  }

  .month-data ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .month-data li {
    padding: 3px 0;
    font-size: 0.9em;
  }

  .student-details {
    margin-bottom: 15px;
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 6px;
  }

  .student-details h4 {
    margin: 0 0 10px 0;
    color: #333;
    font-size: 1em;
  }

  .student-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 5px;
  }

  .student-list li {
    padding: 3px 0;
    font-size: 0.9em;
    color: #555;
  }

  /* Comments analysis styles */
  .comments-section {
    margin-bottom: 20px;
  }

  .comments-section h4 {
    margin: 0 0 10px 0;
    color: #333;
    font-size: 1.1em;
    border-bottom: 1px solid #ddd;
    padding-bottom: 5px;
  }

  .comments-section h5 {
    margin: 0 0 8px 0;
    color: #fab45a;
    font-size: 1em;
  }

  .metrics-grid {
    display: grid;
    gap: 15px;
    margin-top: 10px;
  }

  .metric-item {
    background-color: #f8f9fa;
    padding: 12px;
    border-radius: 6px;
  }

  .metric-item strong {
    display: block;
    margin-bottom: 8px;
    color: #333;
    font-size: 0.9em;
  }

  .metric-bar {
    width: 100%;
    height: 20px;
    background-color: #e9ecef;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 5px;
  }

  .metric-fill {
    height: 100%;
    background-color: #fab45a;
    transition: width 0.3s ease;
    border-radius: 10px;
  }

  .metric-item small {
    color: #666;
    font-size: 0.8em;
  }

  .sample-comments {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 6px;
  }

  .comment-item {
    margin-bottom: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid #e9ecef;
  }

  .comment-item:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }

  .comment-item strong {
    color: #fab45a;
    font-size: 0.9em;
    margin-right: 8px;
  }

  .comment-item span {
    color: #555;
    font-size: 0.9em;
    line-height: 1.4;
  }

  .no-data {
    text-align: center;
    padding: 20px;
    color: #666;
    font-style: italic;
    background-color: #f8f9fa;
    border-radius: 6px;
  }

  .no-data-message {
    text-align: center;
    padding: 30px 20px;
    color: #666;
    background-color: #f8f9fa;
    border-radius: 8px;
    margin: 20px 0;
    border: 1px solid #e9ecef;
  }

  .no-data-message p {
    margin: 10px 0;
    font-size: 1.1em;
  }

  .no-data-message p:first-child {
    font-weight: 500;
    color: #333;
  }

  /* Completion and punctuality specific styles */
  .weekly-trends {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 15px;
    margin-top: 10px;
  }

  .week-item {
    background-color: #f8f9fa;
    padding: 12px;
    border-radius: 6px;
    text-align: center;
  }

  .week-item strong {
    display: block;
    color: #fab45a;
    margin-bottom: 8px;
    font-size: 0.9em;
  }

  .week-stats {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 8px;
  }

  .week-stats span {
    font-size: 0.8em;
    color: #555;
  }

  .week-stats .late-incidents {
    color: #d32f2f;
    font-size: 0.75em;
    font-weight: 500;
  }

  .week-item small {
    color: #666;
    font-size: 0.75em;
  }

  .incident-summary {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 6px;
  }

  .incident-summary p {
    margin: 0 0 10px 0;
    color: #333;
  }

  .incident-summary ul {
    list-style: none;
    padding: 0;
    margin: 0 0 15px 0;
  }

  .incident-summary li {
    padding: 4px 0;
    font-size: 0.9em;
    color: #555;
  }

  .incident-details {
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid #e9ecef;
  }

  .incident-details h5 {
    margin: 0 0 10px 0;
    color: #fab45a;
    font-size: 0.95em;
  }

  .incident-item {
    background-color: white;
    padding: 10px;
    margin-bottom: 8px;
    border-radius: 4px;
    border-left: 3px solid #fab45a;
  }

  .incident-item:last-child {
    margin-bottom: 0;
  }

  .incident-item strong {
    display: block;
    color: #333;
    margin-bottom: 4px;
    font-size: 0.9em;
  }

  .incident-item small {
    display: block;
    color: #666;
    font-size: 0.8em;
    margin-bottom: 4px;
  }

  .incident-item span {
    display: block;
    color: #555;
    font-size: 0.85em;
    line-height: 1.4;
  }

  .no-incidents {
    text-align: center;
    padding: 20px;
    background-color: #e8f5e8;
    border-radius: 6px;
    color: #2e7d32;
    font-weight: 500;
  }

  .no-incidents p {
    margin: 0;
  }

  /* Student attendance specific styles */
  .attendance-details {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 6px;
  }

  .attendance-item {
    background-color: white;
    padding: 12px;
    margin-bottom: 10px;
    border-radius: 4px;
    border-left: 3px solid #fab45a;
  }

  .attendance-item:last-child {
    margin-bottom: 0;
  }

  .attendance-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .attendance-header strong {
    color: #333;
    font-size: 0.9em;
  }

  .attendance-rate {
    background-color: #fab45a;
    color: black;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.8em;
    font-weight: 500;
  }

  .attendance-stats {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .attendance-stats small {
    color: #666;
    font-size: 0.8em;
  }

  .attendance-stats .no-show {
    color: #d32f2f;
    font-weight: 500;
  }

  .more-classes {
    text-align: center;
    padding: 10px;
    color: #666;
    font-style: italic;
  }

  .show-all-button-container {
    text-align: center;
    padding: 15px;
  }

  .show-all-button {
    background-color: #fab45a;
    color: black;
    border: none;
    padding: 10px 20px;
    border-radius: 6px;
    font-size: 0.9em;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.3s ease;
  }

  .show-all-button:hover {
    background-color: #e09a43;
  }

  /* Student feedback specific styles */
  .student-feedback {
    border-left-color: #2e7d32;
    background-color: #f8fff8;
  }

  .rating-info {
    display: flex;
    gap: 15px;
    margin-top: 6px;
    flex-wrap: wrap;
  }

  .rating-info small {
    background-color: #e8f5e8;
    padding: 2px 6px;
    border-radius: 4px;
    color: #2e7d32;
    font-size: 0.75em;
  }

  .rating-info .punctuality-issue {
    background-color: #ffebee;
    color: #d32f2f;
    font-weight: 500;
  }

  .more-reports {
    text-align: center;
    padding: 10px;
    color: #666;
    font-style: italic;
    background-color: #f0f8f0;
    border-radius: 4px;
    margin-top: 8px;
  }

  .missing-surveys {
    display: flex;
    flex-direction: column;
    gap: 1em;
  }

  .missing-item {
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    padding: 1em;
  }

  .missing-item strong {
    color: #dc2626;
  }

  .missing-badge {
    display: inline-block;
    background: #dc2626;
    color: white;
    padding: 0.25em 0.5em;
    border-radius: 4px;
    font-size: 0.75em;
    font-weight: 500;
    margin-top: 0.5em;
  }

  .survey-ratings {
    display: flex;
    gap: 1em;
    margin-top: 0.5em;
    flex-wrap: wrap;
  }

  .survey-ratings span {
    background: #f0f9ff;
    border: 1px solid #bae6fd;
    padding: 0.25em 0.5em;
    border-radius: 4px;
    font-size: 0.8em;
    color: #0369a1;
  }

  .class-details {
    margin-top: 0.5em;
    display: flex;
    flex-direction: column;
    gap: 0.25em;
  }

  .class-details p {
    margin: 0.5em 0;
    font-style: italic;
    color: #555;
  }

  .responsibility-breakdown {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 15px;
  }

  .teacher-incidents,
  .student-incidents {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 6px;
  }

  .student-incidents {
    border-left: 3px solid #fab45a;
  }

  .teacher-incidents {
    border-left: 3px solid #d32f2f;
  }

  .student-cancellation {
    background-color: #fff3e0;
    border-left-color: #fab45a;
  }

  .payment-note {
    background-color: #fab45a;
    color: black;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.75em;
    font-weight: 500;
    margin-top: 4px;
    display: inline-block;
  }

  /* Payment analysis specific styles */
  .paid-class {
    background-color: #fab45a;
    color: black;
  }

  .free-class {
    background-color: #e8f5e8;
    color: #2e7d32;
    border: 1px solid #2e7d32;
  }

  /* Special conditions styling */
  .special-conditions-container {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 4px;
  }

  .special-condition {
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.75em;
    font-weight: 500;
    display: inline-block;
  }

  .ib-condition {
    background-color: #e3f2fd;
    color: #1976d2;
    border: 1px solid #1976d2;
  }

  .special-needs-condition {
    background-color: #fff3e0;
    color: #f57c00;
    border: 1px solid #f57c00;
  }

  .lithuanian-condition {
    background-color: #e8f5e8;
    color: #2e7d32;
    border: 1px solid #2e7d32;
  }

  .total-surcharge {
    background-color: #fab45a;
    color: black;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.75em;
    font-weight: bold;
    display: inline-block;
    border: 2px solid #e09a43;
  }

  .free-reason {
    background-color: #ffebee;
    color: #d32f2f;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.75em;
    font-weight: 500;
    margin-top: 2px;
    display: inline-block;
  }

  .first-time {
    background-color: #fff3e0;
    color: #f57c00;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.75em;
    margin-top: 2px;
    display: inline-block;
  }

  .retained {
    background-color: #e8f5e8;
    color: #2e7d32;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.75em;
    margin-top: 2px;
    display: inline-block;
  }

  /* Upcoming classes styling */
  .upcoming-class {
    opacity: 0.7;
    background-color: #f8f9fa;
    border-left: 4px solid #6c757d;
  }

  .upcoming-class:hover {
    opacity: 0.9;
  }

  /* Class status and payment layout */
  .class-status-payment {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .class-status {
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.75em;
    font-weight: 500;
    text-transform: uppercase;
  }

  .class-status.completed {
    background-color: #d4edda;
    color: #155724;
  }

  .class-status.upcoming {
    background-color: #fff3cd;
    color: #856404;
  }

  /* Student rating summary styles */
  .student-rating-summary {
    background-color: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
    margin-top: 15px;
  }

  .rating-display {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 15px;
  }

  .rating-value {
    font-size: 2.5em;
    color: #fab45a;
    font-weight: bold;
    min-width: 120px;
  }

  .rating-details {
    flex: 1;
  }

  .rating-details p {
    margin: 5px 0;
    color: #666;
  }

  .rating-details p:first-child {
    font-weight: bold;
    color: #333;
    font-size: 1.1em;
  }

  .rating-bar-container {
    width: 100%;
  }

  /* Bonus Criteria Summary Section */
  .bonus-criteria-summary {
    margin-top: 2em;
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 8px;
    box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
    color: black;
  }

  .bonus-criteria-summary h3 {
    margin: 0 0 15px 0;
    color: #333;
    font-size: 1.2em;
    text-align: center;
  }

  .criteria-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 15px;
  }

  .criterion-card {
    display: flex;
    align-items: center;
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
  }

  .criterion-card.eligible {
    border-left: 4px solid #28a745;
    background-color: #f8fff8;
  }

  .criterion-card.not-eligible {
    border-left: 4px solid #d32f2f;
    background-color: #fff3e0;
  }

  .criterion-icon {
    font-size: 2em;
    margin-right: 15px;
    color: #fab45a;
  }

  .criterion-content h4 {
    margin: 0 0 5px 0;
    color: #333;
    font-size: 1em;
  }

  .criterion-value {
    font-size: 1.5em;
    font-weight: bold;
    color: #fab45a;
    margin-bottom: 5px;
  }

  .criterion-status {
    font-size: 0.8em;
    color: #666;
    font-weight: 500;
  }

  .criterion-status.met {
    color: #28a745;
    font-weight: bold;
  }

  .criterion-status.not-met {
    color: #d32f2f;
    font-weight: bold;
  }

  .bonus-eligibility-banner {
    margin-top: 20px;
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
    color: black;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .bonus-eligibility-banner.not-eligible {
    border-left: 4px solid #d32f2f;
    background-color: #fff3e0;
  }

  .bonus-icon {
    font-size: 2em;
    color: #fab45a;
  }

  .bonus-message h4 {
    margin: 0 0 5px 0;
    color: #333;
    font-size: 1em;
  }

  .bonus-message p {
    margin: 0 0 5px 0;
    color: #666;
    font-size: 0.9em;
  }

  .bonus-message strong {
    color: #fab45a;
    font-weight: 500;
  }

  .bonus-message small {
    display: block;
    color: #666;
    font-size: 0.75em;
  }

  @media (max-width: 600px) {
    .teacher-performance-page {
      padding: 1em;
    }

    .month-comparison {
      grid-template-columns: 1fr;
      gap: 10px;
    }

    .student-list {
      grid-template-columns: 1fr;
    }

    .metrics-grid {
      grid-template-columns: 1fr;
    }

    .comment-item {
      padding: 8px 0;
    }

    .comment-item strong,
    .comment-item span {
      display: block;
      margin-bottom: 4px;
    }

    .weekly-trends {
      grid-template-columns: 1fr;
    }

    .week-stats {
      flex-direction: column;
      gap: 2px;
    }

    .week-stats .late-incidents {
      font-size: 0.7em;
    }

    .incident-item strong,
    .incident-item small,
    .incident-item span {
      font-size: 0.85em;
    }

    .attendance-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
    }

    .attendance-stats {
      gap: 1px;
    }

    .attendance-stats small {
      font-size: 0.75em;
    }

    .rating-info {
      flex-direction: column;
      gap: 4px;
    }

    .rating-info small {
      font-size: 0.7em;
    }

    .special-conditions-container {
      flex-direction: column;
      gap: 2px;
    }

    .special-condition,
    .total-surcharge {
      font-size: 0.7em;
      padding: 1px 4px;
    }

    .responsibility-breakdown {
      grid-template-columns: 1fr;
      gap: 10px;
    }

    .payment-breakdown-container {
      grid-template-columns: 1fr;
      gap: 15px;
    }

    .payment-section {
      padding: 12px;
    }

    .payment-section h5 {
      font-size: 0.95em;
    }

    .class-count-list li {
      font-size: 0.85em;
      padding: 5px 0;
    }

    .total-classes {
      padding: 5px 6px;
      font-size: 0.85em;
    }

    .payment-total-only {
      padding: 10px;
      margin-top: 15px;
    }

    .payment-total-only p {
      font-size: 1em;
    }

    .rating-display {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
    }

    .rating-value {
      font-size: 2em;
      min-width: auto;
    }

    .student-rating-summary {
      padding: 15px;
    }

    /* Bonus criteria responsive styles */
    .criteria-grid {
      grid-template-columns: 1fr;
      gap: 10px;
    }

    .criterion-card {
      padding: 12px;
    }

    .criterion-icon {
      font-size: 1.5em;
      margin-right: 10px;
    }

    .criterion-value {
      font-size: 1.2em;
    }

    .bonus-eligibility-banner {
      flex-direction: column;
      text-align: center;
      gap: 15px;
    }

    .bonus-icon {
      font-size: 1.5em;
    }

    .month-dropdown {
      max-width: 100%;
    }
  }
</style>
